import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDidUpdate } from 'rooks';
import { Button, Table } from 'antd';
import {
  SortOrder,
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import moment from 'moment';
import cx from 'classnames';
import pluralize from 'pluralize';
import queryString from 'query-string';

import { SortingDirectionEnum } from 'enums';
import SortButton from 'components/wrappers/BaseSortButton';
import {
  getAgingReportDetails,
  getAgingReportDetailsExcel,
} from 'redux/reducers/homeDashboardSlice';
import { pageDefaultSizeTable } from 'constants/index';
import { getSortOrderRequestValue } from 'utils/sortUtils';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import {
  AgingDetailPagedResponse,
  AgingDetailReport,
} from 'api/backofficeApiClient';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import LastSyncDateSection from 'components/LastSyncDateSection';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  isColumnHeightToFullTableHeight,
  isShowFooterTableOnLastPage,
} from 'utils/tableSettings';
import { getPeriodTitle } from 'utils/reportUtils';

import styles from './AgingReportDetailsPage.module.less';
import commonStyles from '../UserHomePage.module.less';

export default function AgingReportDetailsPage() {
  const { period, customerKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { sortOrder } = queryString.parse(location.search);
  const [sortParam, setSortParam] = useState<SortOrder | undefined>(
    sortOrder || SortingDirectionEnum.Ascend,
  );
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [isAgingReportDetailsDataLoading, setIsAgingReportDetailsDataLoading] =
    useState(false);
  const [agingReportDetailsData, setAgingReportDetailsData] =
    useState<AgingDetailPagedResponse>();
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const dispatch = useAppDispatch();

  const agingReportCountMessage = pluralize(
    'record',
    agingReportDetailsData?.totalCount,
    true,
  );

  useDidUpdate(() => {
    const queryData = {
      sortOrder: sortParam,
    };
    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [sortParam]);

  const loadAgingReportDetailsData = useCallback(async () => {
    if (!period || !customerKey) return;

    setIsAgingReportDetailsDataLoading(true);

    const result = await dispatch(
      getAgingReportDetails({
        pageNumber: currentTablePage - 1,
        sortType: getSortOrderRequestValue(sortParam),
        dateRangeType: +period,
        customerKey: customerKey,
        pageSize: pageDefaultSizeTable,
      }),
    );
    if (getAgingReportDetails.fulfilled.match(result)) {
      setAgingReportDetailsData(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }

    setIsAgingReportDetailsDataLoading(false);
  }, [dispatch, currentTablePage, sortParam, period, customerKey]);

  const loadInitialData = useCallback(async () => {
    await loadAgingReportDetailsData();

    setIsInitialDataLoaded(true);
  }, [loadAgingReportDetailsData]);

  const handleDownloadReport = async () => {
    if (!period || !customerKey) return;

    const result = await dispatch(
      getAgingReportDetailsExcel({
        pageNumber: currentTablePage - 1,
        sortType: getSortOrderRequestValue(sortParam),
        dateRangeType: +period,
        customerKey,
        pageSize: pageDefaultSizeTable,
      }),
    );

    if (getAgingReportDetailsExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const columns = useMemo(
    () => [
      {
        className: 'custom-table-cell--union',
        title: 'Customer Name',
        key: 'customerName',
        render: (
          record: AgingDetailReport,
          row: AgingDetailReport,
          index: number,
        ) => {
          return {
            props: {
              rowSpan: isColumnHeightToFullTableHeight<AgingDetailReport>(
                index,
                agingReportDetailsData?.details || [],
              ),
            },
            children: (
              <Link
                className="wrapped-text"
                to={`/customer/${agingReportDetailsData?.customerKey}`}
              >
                {agingReportDetailsData?.customerCompanyName}
              </Link>
            ),
          };
        },
      },
      {
        className: 'custom-table-cell--union',
        title: 'Customer ID',
        key: 'customerId',
        render: (
          record: AgingDetailReport,
          row: AgingDetailReport,
          index: number,
        ) => {
          return {
            props: {
              rowSpan: isColumnHeightToFullTableHeight<AgingDetailReport>(
                index,
                agingReportDetailsData?.details || [],
              ),
            },
            children: <span>{agingReportDetailsData?.customerKey}</span>,
          };
        },
      },
      {
        className: 'custom-table-cell--union',
        title: 'Terms',
        key: 'terms',
        render: (
          record: AgingDetailReport,
          row: AgingDetailReport,
          index: number,
        ) => {
          return {
            props: {
              rowSpan: isColumnHeightToFullTableHeight<AgingDetailReport>(
                index,
                agingReportDetailsData?.details || [],
              ),
            },
            children: <span>{agingReportDetailsData?.terms}</span>,
          };
        },
      },
      {
        title: 'Worker Name',
        key: 'workerName',
        render: (record: AgingDetailReport) => record.workerName,
      },
      {
        title: 'Document Number',
        key: 'documentNumber',
        render: (record: AgingDetailReport) => record.invoice,
      },
      {
        title: () => {
          return (
            <SortButton
              isActive={true}
              sortType={sortParam}
              label="Invoice Date"
            />
          );
        },
        key: 'invoiceDate',
        sorter: true,
        sortDirections: [
          SortingDirectionEnum.Ascend,
          SortingDirectionEnum.Descend,
        ],
        sortOrder: sortParam,
        render: (record: AgingDetailReport) =>
          record.invDate ? moment.utc(record.invDate).format('MM/DD/YYYY') : '',
      },
      {
        title: 'Due Date',
        key: 'dueDate',
        render: (record: AgingDetailReport) =>
          record.dueDate ? moment.utc(record.dueDate).format('MM/DD/YYYY') : '',
      },
      {
        title: 'Age',
        key: 'age',
        render: (record: AgingDetailReport) => record.age,
      },
      {
        title: getPeriodTitle(period),
        key: getPeriodTitle(period),
        render: (record: AgingDetailReport) => record.balance,
      },
    ],
    [sortParam, period, agingReportDetailsData],
  );

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filter: Record<string, FilterValue | null>,
    sort: SorterResult<AgingDetailReport> | SorterResult<AgingDetailReport>[],
  ) => {
    const sortOrder =
      (sort as SorterResult<AgingDetailReport>).order ||
      SortingDirectionEnum.Ascend;

    setSortParam((prevSortOrder) => {
      if (prevSortOrder !== sortOrder) {
        setCurrentTablePage(1);
      } else {
        setCurrentTablePage(pagination.current || 1);
      }
      return sortOrder;
    });
  };

  if (!agingReportDetailsData) return null;

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-holder">
        <BasePageLabel label="Dashboard details">
          {agingReportDetailsData?.lastSyncDate && (
            <div className="title-line__info">
              <LastSyncDateSection
                syncDate={agingReportDetailsData.lastSyncDate}
                title="Aging report is automatically refreshed every 10 minutes."
              />
            </div>
          )}
        </BasePageLabel>

        <div className="page-box page-inner">
          <div className="page-inner-container">
            <BaseTableHeader
              enableSubTitle={isInitialDataLoaded}
              subTitle={agingReportCountMessage}
              title="AR Aging Report - Detailed"
            >
              <div>
                <Button
                  className={commonStyles.saveBtn}
                  onClick={handleDownloadReport}
                  icon={<span className="icon icon-xls-file" />}
                />
              </div>
            </BaseTableHeader>

            <Table
              className={cx(styles.table, 'custom-table')}
              pagination={{
                total: agingReportDetailsData?.totalCount,
                pageSize: pageDefaultSizeTable,
                current: currentTablePage,
                onChange: setCurrentTablePage,
                showSizeChanger: false,
              }}
              showSorterTooltip={false}
              summary={() => {
                if (
                  !isInitialDataLoaded ||
                  !isShowFooterTableOnLastPage(
                    agingReportDetailsData?.totalCount,
                    pageDefaultSizeTable,
                    agingReportDetailsData?.pageNumber,
                  )
                )
                  return;

                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={8} index={1}>
                      Total
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      {agingReportDetailsData?.totalBalance}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
              onChange={handleTableChange}
              columns={columns}
              dataSource={agingReportDetailsData?.details}
              rowKey={(record) => record.invoice as string}
              loading={isAgingReportDetailsDataLoading}
              scroll={{ x: '100%' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
